/**
 * Secciones de la ficha de reserva: resumen, huéspedes, estancia, cobros y
 * servicios.
 *
 * Divulgación progresiva: el resumen está siempre a la vista y el resto se
 * abre. Cada cabecera lleva la frase que evita tener que abrirla.
 */
import React, { useState } from "react";
import { CaretDown, Plus, Trash, Check } from "@phosphor-icons/react";
import { formatMoney, formatMoneyValue } from "@/lib/pmsSelectors";
import {
  BOOKING_STATUS_LABEL, STAY_STATUS_LABEL, PAYMENT_STATUS_LABEL,
} from "@/data/pmsDemoData";
import { partySummaryLine } from "@/lib/guestsV2Selectors";
import GuestPartyPanel from "@/pages/admin/pms/guests/GuestPartyPanel";
import { REQUEST_STATUS_LABEL, AREA_LABEL } from "@/data/todayV2Demo";
import {
  PAYMENT_EVENT_KINDS, PAYMENT_METHOD_LABEL, CANCELLED_BY_LABEL,
  PARTIAL_AMOUNT_UNKNOWN_LABEL, ECONOMICS_OUT_OF_SCOPE, ECONOMICS_OUT_OF_SCOPE_NOTE,
} from "@/data/reservationsV2Demo";
import { SURFACE, StatusPill, QuickAction, Meta } from "@/pages/admin/pms/v2/V2Primitives";
import AssignUnitPanel from "@/pages/admin/pms/reservations/AssignUnitPanel";

/* -------------------------------------------------------------------------
 * Andamiaje
 * ---------------------------------------------------------------------- */

export function DetailSection({ id, title, summary, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`${SURFACE} mb-2`} id={id} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="w-full text-left px-3.5 py-2.5 min-h-[44px] flex items-center gap-3"
      >
        <span className="min-w-0 flex-1 flex flex-col lg:flex-row lg:items-baseline lg:gap-3">
          <span className="font-serif-dv text-base text-stone-900 dark:text-stone-100 lg:w-40 lg:shrink-0">
            {title}
          </span>
          {summary && (
            <span className="truncate text-[11px] text-stone-500 dark:text-stone-400 lg:flex-1">
              {summary}
            </span>
          )}
        </span>
        <CaretDown
          size={14}
          className={`shrink-0 text-stone-300 dark:text-stone-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-3.5 pb-3.5">{children}</div>}
    </section>
  );
}

function Fact({ label, children, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-400">{label}</div>
      <div className="text-sm text-stone-900 dark:text-stone-100 mt-0.5 break-words">{children}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Resumen
 * ---------------------------------------------------------------------- */

const STAY_TONE = { pre_arrival: "blue", checked_in: "emerald", checked_out: "stone" };
const PAY_TONE = {
  paid: "emerald", authorized: "blue", partially_paid: "amber", pending: "amber",
  unpaid: "red", failed: "red", partially_refunded: "stone", refunded: "stone",
};
const BOOK_TONE = {
  confirmed: "emerald", pending: "amber", hold: "amber", cancelled: "stone", no_show: "red",
};

/**
 * Los tres ejes se enseñan por separado a propósito: una reserva confirmada
 * puede estar sin pagar, y una cancelada no es lo mismo que una que ya salió
 * (§8). La asignación es un cuarto dato, no un estado de la reserva.
 */
export function OverviewSection({ view, unitLabel }) {
  const r = view.reservation;
  return (
    <div className="mb-3" data-testid="detail-overview">
      <div className={`${SURFACE} p-3.5`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-serif-dv text-2xl text-stone-900 dark:text-stone-100 truncate">
              {r.guest?.name || "—"}
            </h1>
            <Meta items={[
              `${r.check_in} → ${r.check_out}`,
              `${r.nights} noches`,
              view.unitType?.name,
              view.channel?.label,
            ]} className="mt-1" />
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono text-xs text-stone-500 dark:text-stone-400">{r.reservation_id}</div>
            {view.externalRef && (
              <div className="text-[11px] text-stone-400 mt-0.5" data-testid="detail-external-ref">
                {view.externalRef}
              </div>
            )}
            {view.viaChannelManager && (
              <div
                className="text-[11px] text-stone-400 mt-0.5"
                title="Cuando exista channel manager, la reserva llegará por él. Hoy no hay ninguno conectado."
                data-testid="detail-channel-manager"
              >
                Canal externo · sin conectar
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="detail-status-axes">
          <Axis label="Reserva" testId="axis-booking">
            <StatusPill tone={BOOK_TONE[r.booking_status] || "stone"}>
              {BOOKING_STATUS_LABEL[r.booking_status]}
            </StatusPill>
          </Axis>
          <Axis label="Estancia" testId="axis-stay">
            <StatusPill tone={STAY_TONE[r.stay_status] || "stone"}>
              {STAY_STATUS_LABEL[r.stay_status]}
            </StatusPill>
          </Axis>
          <Axis label="Cobro" testId="axis-payment">
            <StatusPill tone={PAY_TONE[r.payment_status] || "stone"}>
              {PAYMENT_STATUS_LABEL[r.payment_status]}
            </StatusPill>
          </Axis>
          <Axis label="Asignación" testId="axis-assignment">
            <StatusPill tone={view.assignedUnit ? "stone" : "amber"} dot={false}>
              {view.assignedUnit ? view.assignedUnit.villa_id : `${unitLabel.singular} sin asignar`}
            </StatusPill>
          </Axis>
        </div>

        {view.attention.length > 0 && (
          <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800" data-testid="detail-attention">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1.5">
              Necesita atención
            </div>
            <div className="flex flex-wrap gap-1.5">
              {view.attention.map((a) => (
                <StatusPill key={a.key} tone={a.tone} dot={false} testId={`detail-attention-${a.key}`}>
                  {a.label}
                </StatusPill>
              ))}
            </div>
          </div>
        )}

        {r.cancellation && (
          <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800" data-testid="detail-cancellation">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1.5">Cancelación</div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <Fact label="Fecha">{String(r.cancellation.cancelled_at).slice(0, 10)}</Fact>
              <Fact label="Origen">
                {CANCELLED_BY_LABEL[r.cancellation.cancelled_by] || r.cancellation.cancelled_by}
              </Fact>
              <Fact label="Retenido">
                {formatMoney(r.cancellation.retained_revenue_eur || 0, view.money.currency)}
              </Fact>
              <Fact label="Reembolsado">
                {formatMoney(r.cancellation.refunded_eur || 0, view.money.currency)}
              </Fact>
            </div>
            {r.cancellation.reason && (
              <p className="text-[11px] text-stone-500 dark:text-stone-400 mt-2">
                Motivo: {r.cancellation.reason}
              </p>
            )}
            <p className="text-[11px] text-stone-400 mt-1">
              La reserva se conserva entera: sigue apareciendo en búsquedas e informes. La
              compensación del reparto por cancelaciones sigue sin decidirse.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Axis({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Huéspedes
 * ---------------------------------------------------------------------- */

/**
 * Huéspedes — las personas de la reserva, no un duplicado de la reserva.
 *
 * Todo lo que se ve aquí sale del modelo de huésped (`guestsV2Selectors`), el
 * mismo que lee Hoy. La ficha no tiene su propia idea de quién viene ni de qué
 * falta: si las dos pantallas discrepan es un fallo, y por eso comparten
 * fuente y componente.
 */
export function GuestsSection({ view, onVerifyDocument, onFileImmigration }) {
  const party = view.readiness.party;
  return (
    <DetailSection
      title="Huéspedes"
      testId="detail-guests"
      summary={partySummaryLine(party)}
    >
      <GuestPartyPanel
        view={party}
        onVerifyDocument={onVerifyDocument}
        onFileImmigration={onFileImmigration}
        compact
      />

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
        <QuickAction label="Ver perfil del huésped" disabled testId="guest-profile-link" />
        <p className="text-[11px] text-stone-400 mt-1">
          El perfil de huésped —el histórico de estancias de una persona a lo
          largo del tiempo— es un módulo futuro: todavía no existe una pantalla
          a la que ir.
        </p>
      </div>
    </DetailSection>
  );
}

/* -------------------------------------------------------------------------
 * Estancia (incluye la asignación de unidad física)
 * ---------------------------------------------------------------------- */

const LATE_CHECKOUT_LABEL = {
  requested: "Solicitada",
  granted: "Aprobada",
  denied: "Rechazada",
  paid: "Pagada",
};

export function StaySection({ view, settings, unitLabel, onAssign, onUnassign }) {
  const stay = view.stay;
  return (
    <DetailSection
      title="Estancia"
      testId="detail-stay"
      summary={`${stay.nights} noches · entrada ${stay.check_in_time} · salida ${stay.check_out_time}`}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Fact label="Entrada">{stay.check_in}</Fact>
        <Fact label="Salida">{stay.check_out}</Fact>
        <Fact label="Noches">{stay.nights}</Fact>
        {/* La hora de entrada por defecto ya está en el resumen de arriba y
            en la nota; aquí solo cabe lo que el huésped haya dicho. */}
        <Fact label="Llegada prevista" testId="stay-eta">
          {stay.eta || (
            <span className="text-stone-400" title="El huésped no ha dicho a qué hora llega">
              Sin hora
            </span>
          )}
        </Fact>
      </div>

      {/* El aterrizaje es otro hecho distinto de la llegada a la villa, y se
          dice aparte: confundirlos hacía esperar al huésped antes de tiempo. */}
      {(stay.arrival_mode_label || stay.landing_time) && (
        <div className="mt-3 text-xs text-stone-500 dark:text-stone-400" data-testid="stay-arrival">
          <Meta items={[
            stay.arrival_mode_label,
            stay.arrival_reference,
            stay.landing_time ? `${stay.landing_label?.toLowerCase()} ${stay.landing_time}` : null,
            stay.transfer_label,
          ]} />
        </div>
      )}

      <p className="text-[11px] text-stone-400 mt-2" data-testid="stay-times-note">
        Las horas de entrada y salida por defecto vienen de Ajustes. La llegada
        prevista la dice el huésped en su pre-check-in; si no la ha dicho, no
        hay hora que enseñar.
      </p>

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 flex flex-wrap gap-1.5">
        {stay.late_checkout ? (
          <StatusPill
            tone={stay.late_checkout.status === "granted" ? "emerald" : "stone"}
            dot={false}
            testId="stay-late-checkout"
            title={stay.late_checkout.reason}
          >
            Salida tardía: {LATE_CHECKOUT_LABEL[stay.late_checkout.status] || stay.late_checkout.status}
            {stay.late_checkout.until ? ` · ${stay.late_checkout.until}` : ""}
          </StatusPill>
        ) : (
          <StatusPill tone="stone" dot={false} testId="stay-late-checkout">
            Salida tardía: sin solicitar
          </StatusPill>
        )}
        <StatusPill tone="stone" dot={false}>Entrada anticipada: sin solicitar</StatusPill>
      </div>
      <p className="text-[11px] text-stone-400 mt-1.5">
        Ni la salida tardía ni la entrada anticipada se aprueban solas: dependerán de la
        disponibilidad, que decide otra parte del producto.
      </p>

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800">
        <AssignUnitPanel
          view={view}
          settings={settings}
          unitLabel={unitLabel}
          onAssign={onAssign}
          onUnassign={onUnassign}
        />
      </div>
    </DetailSection>
  );
}

/* -------------------------------------------------------------------------
 * Cobros
 * ---------------------------------------------------------------------- */

export function PaymentsSection({ view, onAddPayment }) {
  const money = view.money;
  const currency = money.currency;
  const [kind, setKind] = useState("deposit");
  const [amount, setAmount] = useState("");

  const summary = money.partial_amount_unknown
    ? PARTIAL_AMOUNT_UNKNOWN_LABEL
    : (money.outstanding_eur > 0
      ? `${formatMoney(money.paid_eur, currency)} cobrado · ${formatMoney(money.outstanding_eur, currency)} pendiente`
      : `${formatMoney(money.paid_eur, currency)} cobrado · sin saldo pendiente`);

  return (
    <DetailSection title="Cobros" testId="detail-payments" summary={summary}>
      <dl className="text-sm" data-testid="payments-summary">
        <MoneyRow
          label="Alojamiento (impuesto incluido)"
          value={money.accommodation_eur}
          currency={currency}
          testId="money-accommodation"
        />
        <MoneyRow label="Servicios" value={money.services_eur} currency={currency} testId="money-services" />
        <MoneyRow
          label="Descuento"
          value={-money.discount_eur}
          currency={currency}
          muted={money.discount_eur === 0}
        />
        <MoneyRow label="Total" value={money.total_eur} currency={currency} strong testId="money-total" />
        <MoneyRow
          label={`del cual ${view.economics.tax.label} (${money.tax_pct} %)`}
          value={money.tax_included_eur}
          currency={currency}
          muted
          testId="money-tax"
        />
        {money.partial_amount_unknown ? (
          <div
            className="flex items-baseline justify-between gap-3 py-0.5"
            data-testid="money-partial-unknown"
          >
            <dt className="text-amber-700 dark:text-amber-300">Cobrado</dt>
            <dd className="text-amber-700 dark:text-amber-300 text-right">
              {PARTIAL_AMOUNT_UNKNOWN_LABEL}
            </dd>
          </div>
        ) : (
          <>
            <MoneyRow label="Cobrado" value={money.paid_eur} currency={currency} testId="money-paid" />
            <MoneyRow
              label="Pendiente"
              value={money.outstanding_eur}
              currency={currency}
              strong
              testId="money-outstanding"
            />
          </>
        )}
      </dl>
      <p className="text-[11px] text-stone-400 mt-1.5" data-testid="money-tax-note">
        El precio de alojamiento se muestra CON el impuesto incluido: {view.economics.tax.label} al{" "}
        {money.tax_pct} % sobre {String(view.economics.tax.base_label).toLowerCase()}, configurado en
        Ajustes › Finanzas. Esto es un resumen de la reserva, no contabilidad.
      </p>
      {money.partial_amount_unknown && (
        <p className="text-[11px] text-stone-400 mt-1" data-testid="money-partial-unknown-note">
          Consta un cobro parcial, pero no su importe. No se reparte con ningún porcentaje de
          depósito supuesto: el importe aparecerá cuando exista el movimiento.
        </p>
      )}

      <ReservationEconomics economics={view.economics} />

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800">
        <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1.5">Movimientos</div>
        {view.events.length === 0 ? (
          <p className="text-sm text-stone-500 dark:text-stone-400">Todavía no hay cobros.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="payment-events">
            {view.events.map((e) => (
              <li key={e.event_id} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0">
                  <span className="text-stone-900 dark:text-stone-100">
                    {PAYMENT_EVENT_KINDS[e.kind]?.label || e.kind}
                  </span>
                  <Meta items={[
                    PAYMENT_METHOD_LABEL[e.method] || e.method,
                    e.at ? String(e.at).slice(0, 10) : null,
                    e.note,
                  ]} />
                </span>
                <span className="tabular-nums shrink-0 text-stone-900 dark:text-stone-100">
                  {(PAYMENT_EVENT_KINDS[e.kind]?.sign ?? 1) < 0 ? "−" : ""}
                  {formatMoney(e.amount_eur, currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {money.has_derived_events && (
          <p className="text-[11px] text-stone-400 mt-1.5" data-testid="payments-derived-note">
            Movimientos deducidos del estado de pago para poder enseñar la ficha. No hay ninguna
            pasarela conectada.
          </p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 flex flex-wrap items-end gap-2">
        <label className="min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Tipo</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="payment-kind"
            className="dv-input text-xs min-h-[44px]"
          >
            {Object.entries(PAYMENT_EVENT_KINDS).map(([value, v]) => (
              <option key={value} value={value}>{v.label}</option>
            ))}
          </select>
        </label>
        <label className="min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Importe</span>
          <input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="payment-amount"
            className="dv-input text-xs min-h-[44px] w-28"
          />
        </label>
        <QuickAction
          label="Registrar"
          primary
          disabled={!(Number(amount) > 0)}
          onClick={() => {
            onAddPayment({ kind, amount_eur: Number(amount) });
            setAmount("");
          }}
          testId="payment-add"
        />
        <p className="text-[11px] text-stone-400 basis-full">
          Registrar un movimiento no cambia el estado de cobro de la reserva: son ejes distintos y
          lo decide una persona.
        </p>
      </div>
    </DetailSection>
  );
}

/**
 * Una fila de importe. Acepta `money` (la forma explícita `{ amount, currency }`)
 * o el par suelto `value` + `currency`; en los dos casos el importe viaja con
 * su moneda, nunca con una elegida en Ajustes.
 */
function MoneyRow({ label, value, currency, money, strong, muted, negative, testId }) {
  const amount = money ? money.amount : value;
  const text = money ? formatMoneyValue(money) : formatMoney(value, currency);
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5" data-testid={testId}>
      <dt className={`${muted ? "text-stone-400" : "text-stone-600 dark:text-stone-400"} ${strong ? "text-stone-900 dark:text-stone-100" : ""}`}>
        {label}
      </dt>
      <dd className={`tabular-nums ${strong ? "text-stone-900 dark:text-stone-100" : muted ? "text-stone-400" : "text-stone-700 dark:text-stone-300"}`}>
        {amount === 0 && muted ? "—" : `${negative ? "−" : ""}${text}`}
      </dd>
    </div>
  );
}

/**
 * Resultado de UNA reserva.
 *
 * Llega hasta el ingreso neto de la estancia y ahí se para. Los sueldos, los
 * suministros, el mantenimiento general, los seguros, la comisión de gestión y
 * el beneficio del propietario NO se calculan aquí: son costes de un periodo,
 * no de una reserva, y se reparten en Finanzas · Cierre mensual. Decirlo en la
 * pantalla evita que nadie lea este neto como si fuera el beneficio.
 */
function ReservationEconomics({ economics }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800" data-testid="reservation-economics">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1.5">
        Resultado de la reserva
      </div>
      <dl className="text-sm">
        <MoneyRow label="Alojamiento" money={economics.accommodation} />
        <MoneyRow label="Servicios" money={economics.services} />
        <MoneyRow label="Bruto de la reserva" money={economics.gross} strong />
        <MoneyRow
          label={`${economics.tax.label} (${economics.tax.pct} %, ya incluido)`}
          money={economics.tax.amount}
          negative
          testId="economics-tax"
        />
        {economics.channel_cost ? (
          <MoneyRow
            label={`${economics.channel_cost.label} (${economics.channel_cost.pct} %)`}
            money={economics.channel_cost.amount}
            negative
            testId="economics-channel"
          />
        ) : (
          <div className="flex items-baseline justify-between gap-3 py-0.5" data-testid="economics-channel-none">
            <dt className="text-stone-400">Comisión de canal</dt>
            <dd className="text-stone-400">Sin comisión · canal directo</dd>
          </div>
        )}
        {economics.processing && (
          <MoneyRow
            label={`Procesamiento de pago (${economics.processing.pct} %)`}
            money={economics.processing.amount}
            negative
            testId="economics-processing"
          />
        )}
        <MoneyRow
          label="Ingreso neto de la reserva"
          money={economics.net_revenue}
          strong
          testId="economics-net"
        />
      </dl>

      {economics.channel_cost && !economics.channel_cost.approved && (
        <p className="text-[11px] text-stone-400 mt-1.5" data-testid="economics-channel-note">
          El porcentaje del canal sale de Ajustes › Finanzas y todavía es un supuesto de
          modelado, no una condición firmada.
        </p>
      )}
      {economics.processing?.fixed_excluded && (
        <p className="text-[11px] text-stone-400 mt-1">
          El coste fijo por transacción está configurado en moneda operativa y no se convierte
          aquí: la vista previa no hace conversión de divisa.
        </p>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="economics-scope-toggle"
        className="mt-2 min-h-[44px] text-[11px] text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 underline"
      >
        Qué no se calcula aquí
      </button>
      {open && (
        <div className="mt-1" data-testid="economics-scope">
          <ul className="text-[11px] text-stone-500 dark:text-stone-400 list-disc pl-4 space-y-0.5">
            {ECONOMICS_OUT_OF_SCOPE.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="text-[11px] text-stone-400 mt-1.5">{ECONOMICS_OUT_OF_SCOPE_NOTE}</p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Servicios
 * ---------------------------------------------------------------------- */

export function ServicesSection({ view, settings, onAddService, onRemoveService }) {
  const [serviceId, setServiceId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const catalogue = (settings.services || []).filter((s) => s.active);

  return (
    <DetailSection
      title="Servicios"
      testId="detail-services"
      summary={view.services.length === 0
        ? "Sin servicios añadidos"
        : `${view.services.length} servicios · ${formatMoney(view.money.services_eur, view.money.currency)}`}
    >
      {view.services.length === 0 ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">
          Esta reserva no tiene servicios todavía.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="services-list">
          {view.services.map((s) => (
            <li key={s.request_id} className="flex items-start justify-between gap-3" data-testid={`service-${s.request_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-stone-900 dark:text-stone-100">
                  {s.label}
                  {s.quantity > 1 && <span className="text-stone-400"> × {s.quantity}</span>}
                </div>
                <Meta items={[
                  AREA_LABEL[s.area],
                  REQUEST_STATUS_LABEL[s.status],
                  s.scheduled_for ? `para ${s.scheduled_for}` : null,
                  s.service_inactive ? "desactivado en Ajustes" : null,
                ]} />
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <span className="tabular-nums text-sm text-stone-900 dark:text-stone-100">
                  {formatMoney(s.line_total_eur, view.money.currency)}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveService(s.request_id)}
                  aria-label={`Quitar ${s.label}`}
                  data-testid={`service-remove-${s.request_id}`}
                  className="w-11 h-11 flex items-center justify-center text-stone-400 hover:text-red-600"
                >
                  <Trash size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-stone-800 flex flex-wrap items-end gap-2">
        <label className="min-w-0 flex-1">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
            Añadir del catálogo
          </span>
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            data-testid="service-picker"
            className="dv-input w-full text-xs min-h-[44px]"
          >
            <option value="">Elegir servicio…</option>
            {catalogue.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {`${s.name} · ${formatMoney(s.sale_price_eur, view.money.currency)}`}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">Cantidad</span>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            data-testid="service-quantity"
            className="dv-input text-xs min-h-[44px] w-20"
          />
        </label>
        <QuickAction
          label="Añadir"
          icon={Plus}
          primary
          disabled={!serviceId}
          onClick={() => {
            onAddService({ service_id: serviceId, quantity });
            setServiceId("");
            setQuantity(1);
          }}
          testId="service-add"
        />
        <p className="text-[11px] text-stone-400 basis-full">
          El catálogo y los precios vienen de Ajustes › Servicios. Añadir aquí actualiza el total
          de la reserva y la petición aparece también en Hoy.
        </p>
      </div>
    </DetailSection>
  );
}
